// Pure helpers for the Branch Cash expense / receipt screens - unit-tested without rendering. The server re-validates and decides
// everything (limits, evidence, separation of duties, branch grants); these only stop an obviously incomplete form and decide which
// buttons are worth showing. Hiding a button is never the security mechanism.

export const isMoney = (v: string): boolean => /^\d+(\.\d{1,2})?$/.test(v.trim());
const paise = (v: string): number => { const [w, f = ''] = v.trim().split('.'); return Number(w) * 100 + Number(f.padEnd(2, '0')); };

export const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' }, { value: 'UPI', label: 'UPI' }, { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' }, { value: 'OTHER', label: 'Other' },
];
export const RECEIPT_SOURCES = [
  { value: 'ALLOCATION_FROM_OFFICE', label: 'Cash allocation from office' }, { value: 'OWNER_INTRODUCED', label: 'Owner introduced cash' },
  { value: 'MISC_INCOME', label: 'Miscellaneous income' }, { value: 'OTHER', label: 'Other' },
];

export const EXPENSE_STATUS_LABEL: Record<string, string> = {
  PENDING_APPROVAL: 'Awaiting approval', APPROVED: 'Approved - not paid', REJECTED: 'Rejected', PAID: 'Paid', CANCELLED: 'Cancelled', REVERSED: 'Reversed',
};
export const RECEIPT_STATUS_LABEL: Record<string, string> = {
  PENDING_APPROVAL: 'Awaiting approval', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', CANCELLED: 'Cancelled', REVERSED: 'Reversed',
};
export const APPROVAL_REASON_LABEL: Record<string, string> = {
  LIMIT_EXCEEDED: 'Over the daily limit', EVIDENCE_EXCEPTION: 'No evidence (exception)', SOURCE_RULE: 'Source rule', AMOUNT_THRESHOLD: 'Amount threshold',
};
export const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' | 'info' =>
  s === 'PAID' || s === 'CONFIRMED' ? 'success' : s === 'PENDING_APPROVAL' ? 'warning' : s === 'APPROVED' ? 'info' : s === 'REJECTED' || s === 'REVERSED' ? 'error' : 'default';

/** Is supporting evidence required for this amount? threshold: category's, else branch's, else Rs 500 (0 = always). Rupees. */
export function evidenceRequired(amount: string, categoryThreshold: number | null | undefined, branchThreshold: number | null | undefined = null): boolean {
  if (!isMoney(amount)) return false;
  const t = categoryThreshold ?? branchThreshold ?? 500;
  return paise(amount) >= Math.round(t * 100);
}

export interface Usage { limit: number | null; used: number; warningLevel: number; warningPercents?: number[] }
/** What the server is about to decide for this amount (mirror of the limit rule, for a heads-up only): approval iff used + amount > limit. */
export function limitPreview(usage: Usage | null | undefined, amount: string): { needsApproval: boolean; after: number | null } {
  if (!usage || !isMoney(amount)) return { needsApproval: false, after: null };
  const after = Math.round((usage.used * 100) + paise(amount)) / 100;
  return { needsApproval: usage.limit !== null && Math.round(after * 100) > Math.round(usage.limit * 100), after };
}
export function warningText(usage: Usage | null | undefined): string {
  if (!usage || usage.limit === null) return '';
  const pct = usage.warningPercents?.[Math.max(usage.warningLevel - 1, 0)];
  if (usage.used > usage.limit) return 'Today\'s expenses are over the limit - every further expense needs approval.';
  if (usage.warningLevel === 3) return 'Today\'s expenses have reached the limit - the next expense needs approval.';
  if (usage.warningLevel > 0) return `Today's expenses have reached ${pct ?? ''}% of the limit.`;
  return '';
}

export interface ExpenseFormValues { categoryId: string; description: string; amount: string; paymentMode: string; evidenceRef: string; evidenceExceptionReason: string }
/** `fileCount` = evidence files already attached in the form; one counts as evidence just like a reference does. */
export function expenseFormError(f: ExpenseFormValues, evidenceNeeded: boolean, fileCount = 0): string {
  if (!f.categoryId) return 'Choose a category';
  if (!f.description.trim()) return 'Describe the expense';
  if (!isMoney(f.amount) || paise(f.amount) <= 0) return 'Enter the amount as a rupee value greater than zero';
  if (evidenceNeeded && fileCount === 0 && !f.evidenceRef.trim() && !f.evidenceExceptionReason.trim()) return 'Attach a file or give an evidence reference, or explain why there is none';
  return '';
}

export interface ReceiptFormValues { source: string; amount: string; reason: string }
export function receiptFormError(f: ReceiptFormValues): string {
  if (!f.source) return 'Choose the source of the cash';
  if (!isMoney(f.amount) || paise(f.amount) <= 0) return 'Enter the amount as a rupee value greater than zero';
  if (!f.reason.trim()) return 'Give the reason for the receipt';
  return '';
}

type Has = (permission: string) => boolean;
export type ExpenseAction = 'approve' | 'reject' | 'pay' | 'cancel' | 'reverse' | 'resubmit';
/** Buttons worth showing on an expense row: the permission AND the state; the server still refuses anything it should. */
export function expenseActions(e: { status: string; createdById: string; submittedById: string; paymentMode: string }, me: string | undefined, has: Has): ExpenseAction[] {
  const out: ExpenseAction[] = [];
  const own = e.createdById === me || e.submittedById === me;
  if (e.status === 'PENDING_APPROVAL' && has('cash:expense_approve') && !own) out.push('approve', 'reject');
  if (e.status === 'APPROVED' && has('cash:expense_pay')) out.push('pay');
  if ((e.status === 'PENDING_APPROVAL' || e.status === 'APPROVED') && (has('cash:expense_create') || has('cash:expense_approve'))) out.push('cancel');
  if (e.status === 'PAID' && has('cash:expense_reverse')) out.push('reverse');
  if (e.status === 'REJECTED' && has('cash:expense_create')) out.push('resubmit');
  return out;
}

export type ReceiptAction = 'approve' | 'reject' | 'cancel' | 'reverse';
export function receiptActions(r: { status: string; createdById: string }, me: string | undefined, has: Has): ReceiptAction[] {
  const out: ReceiptAction[] = [];
  if (r.status === 'PENDING_APPROVAL' && has('cash:receipt_approve') && r.createdById !== me) out.push('approve', 'reject');
  if (r.status === 'PENDING_APPROVAL' && (has('cash:receipt_record') || has('cash:receipt_approve'))) out.push('cancel');
  if (r.status === 'CONFIRMED' && has('cash:receipt_reverse')) out.push('reverse');
  return out;
}

/** Actions that must record a reason on the server. */
export const NEEDS_REASON: ReadonlySet<string> = new Set(['reject', 'cancel', 'reverse']);
